import React, { useCallback, useMemo } from "react";
import { useForm } from "react-final-form";
import { dynamicTypes } from "shared/rsDictionary";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import RangeValue from "rs-components/RangeValue";
import NumberInput from "rs-components/Input/components/NumberInput";
import { getErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { DynamicPropKeys, DynamicRestrictionKeys, DynamicType, } from "store/cell-model/cell-model.types";
import * as styles from "../../styles.module.scss";
export const DEFAULT_DYNAMIC_LABELS = {
    type: "Uncertainty",
    mean: "Max rate",
    sd: "Spread",
    from: "Min",
    to: "Max",
    value: "Known Value",
};
const CommonDynamicFields = ({ name, values, restrictions = {}, editable = true, serverErrors, serverErrorsName, resetServerErrors, children, dynamicLabels = DEFAULT_DYNAMIC_LABELS, suffix, customErrorMessage, }) => {
    const form = useForm();
    const resetFormFields = useCallback((type, form) => {
        const formRegisteredFields = form.getRegisteredFields();
        const currentFields = formRegisteredFields.filter((fieldName) => fieldName.includes(`${name}.`) && fieldName !== `${name}.${DynamicPropKeys.type}`);
        form.batch(() => {
            currentFields.forEach((fieldName) => form.change(fieldName, undefined));
        });
        form.change(`${name}.${DynamicPropKeys.type}`, type);
        resetServerErrors && resetServerErrors();
    }, [name, resetServerErrors]);
    const uncertaintyFieldRestrictions = useMemo(() => Object.keys(restrictions).reduce((acc, type) => ({
        ...acc,
        ...restrictions[type]?.Type,
    }), {}), [restrictions]);
    return (React.createElement("div", { className: styles.wrapper },
        React.createElement(Input, { component: SelectInput, autoFocus: true, value: dynamicTypes.find((item) => item.value === values?.type), name: `${name}.${DynamicPropKeys.type}`, label: DEFAULT_DYNAMIC_LABELS.type, items: dynamicTypes, placeholder: "Select", onChange: (value) => resetFormFields(value, form), itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, readonly: !editable, disableFormChange: true, classes: {
                container: styles.selectContainer,
                component: styles.selectWrapper,
                label: styles.label,
            }, restrictions: uncertaintyFieldRestrictions }),
        values?.type === DynamicType.RANGE && (React.createElement(RangeValue, { from: {
                component: NumberInput,
                name: `${name}.${DynamicPropKeys.from}`,
                restrictions: {
                    ...restrictions?.[DynamicType.RANGE]?.["From"],
                    IsNumber: true,
                },
                externalError: getErrorByPropertyName([`${serverErrorsName}.${DynamicRestrictionKeys.from}`, serverErrorsName], serverErrors)?.text,
                classes: { container: styles.rangeField, label: styles.label },
                label: dynamicLabels.from,
                unit: suffix,
                onChange: resetServerErrors,
            }, to: {
                component: NumberInput,
                name: `${name}.${DynamicPropKeys.to}`,
                restrictions: { ...restrictions?.[DynamicType.RANGE]?.["To"], IsNumber: true },
                externalError: getErrorByPropertyName([`${serverErrorsName}.${DynamicRestrictionKeys.to}`, serverErrorsName], serverErrors)?.text,
                classes: { container: styles.rangeField, label: styles.label },
                label: dynamicLabels.to,
                unit: suffix,
                onChange: resetServerErrors,
            }, editable: editable, customErrorMessage: customErrorMessage, className: styles.ml24 })),
        values?.type === DynamicType.DISTRIBUTION && (React.createElement("div", { className: styles.flex },
            React.createElement(Input, { name: `${name}.${DynamicPropKeys.mean}`, component: NumberInput, label: dynamicLabels.mean, value: values.mean, readonly: !editable, classes: {
                    container: styles.field,
                    label: styles.label,
                }, onChange: resetServerErrors, externalError: getErrorByPropertyName([`${serverErrorsName}.${DynamicRestrictionKeys.mean}`, serverErrorsName], serverErrors)?.text, restrictions: {
                    ...restrictions[DynamicType.DISTRIBUTION]?.Mean,
                    IsNumber: true,
                }, unit: suffix }),
            React.createElement(Input, { name: `${name}.${DynamicPropKeys.sd}`, component: NumberInput, label: dynamicLabels.sd, value: values.sd, readonly: !editable, classes: {
                    container: styles.field,
                    label: styles.label,
                }, onChange: resetServerErrors, externalError: getErrorByPropertyName([`${serverErrorsName}.${DynamicRestrictionKeys.sd}`, serverErrorsName], serverErrors)?.text, restrictions: {
                    ...restrictions[DynamicType.DISTRIBUTION]?.SD,
                    IsNumber: true,
                } }))),
        values?.type === DynamicType.FIXED && (React.createElement(Input, { name: `${name}.${DynamicPropKeys.value}`, component: NumberInput, label: dynamicLabels.value, value: values.value, readonly: !editable, classes: {
                container: styles.field,
                label: styles.label,
            }, onChange: resetServerErrors, externalError: getErrorByPropertyName([`${serverErrorsName}.${DynamicRestrictionKeys.value}`, serverErrorsName], serverErrors)?.text, restrictions: {
                ...restrictions[DynamicType.FIXED]?.Value,
                IsNumber: true,
            }, unit: suffix })),
        values?.type && children));
};
export default CommonDynamicFields;
